import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebaseConfig';
import SubscriptionRequired from '../components/SubscriptionRequired';
import { Navigate } from 'react-router-dom';
import { useIdleTimer } from '../hooks/useIdleTimer'; // Import the new hook

const AuthContext = createContext();

// Define the inactivity timeout duration in milliseconds (e.g., 15 minutes)
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const subscriptionListenerUnsubscribe = useRef(null); // Ref to store the unsubscribe function

  const subscriptionStatus = subscriptionDetails ? {
    state: subscriptionDetails.subscription_status || 'unknown',
    status: subscriptionDetails.subscription_status || 'unknown',
    trial_end: subscriptionDetails.trial_end || null, // Pass through trial_end if it exists
    current_period_end: subscriptionDetails.current_period_end || null // Pass through current_period_end
  } : null;

  const isLoading = loading; // Loading is now just based on initial auth check

  const handleLogout = useCallback(async () => {
    setError(null);
    setLoading(true); // Indicate loading during logout

    try {
      await signOut(auth);
      setCurrentUser(null);
      console.log("User logged out due to inactivity or manual action.");
    } catch (error) {
      console.error("Logout error:", error.code, error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setError]); // Dependencies for logout - keep setError as it's used

  const { clear: clearIdleTimer } = useIdleTimer(
    () => {
        if (auth.currentUser) {
            console.log("AuthContext: Idle timer expired. Logging out...");
            handleLogout();
        } else {
            console.log("AuthContext: Idle timer expired, but no user logged in. Skipping logout.");
        }
    },
    currentUser ? INACTIVITY_TIMEOUT_MS : null
  );

  useEffect(() => {
    setLoading(true); // Ensure loading is true at the start of the effect

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // Detach previous listener if exists
      if (subscriptionListenerUnsubscribe.current) {
        console.log("AuthContext: Detaching existing Firestore listener.");
        subscriptionListenerUnsubscribe.current();
        subscriptionListenerUnsubscribe.current = null;
      }
      // Reset states on auth change
      setCurrentUser(null);
      setSubscriptionDetails(null);
      // Keep loading true until we determine the final state for this auth change
      setLoading(true); 

      if (user) {
        console.log(`AuthContext: User logged in: ${user.uid}`);
        setCurrentUser(user); // Set user first
        // DO NOT set loading = false here

        const userDocRef = doc(db, 'Psyntel', user.uid, 'user_info', 'info');
        console.log(`AuthContext: Setting up Firestore listener for path: ${userDocRef.path}`);

        subscriptionListenerUnsubscribe.current = onSnapshot(userDocRef,
          (snap) => {
            if (snap.exists()) {
              const data = snap.data();
              console.log("AuthContext: Firestore snapshot received:", data);
              setSubscriptionDetails({
                subscription_status: data?.subscription_status || 'unknown',
                trial_end: data?.trial_end || null,
                current_period_end: data?.current_period_end || null,
              });
            } else {
              console.warn(`AuthContext: User info document does not exist for UID: ${user.uid}`);
              setSubscriptionDetails({ subscription_status: 'unknown' }); // Handle as appropriate
            }
            // Set loading false *after* first snapshot received
            setLoading(false); 
          },
          (error) => {
            console.error("AuthContext: Firestore listener error:", error);
            setError(`Error listening to subscription status: ${error.message}`);
            setSubscriptionDetails({ subscription_status: 'error', error: error.message });
            // Set loading false *after* listener error
            setLoading(false); 
          }
        );

      } else {
        console.log("AuthContext: User logged out.");
        // If no user, we are definitively done loading.
        setCurrentUser(null);
        setSubscriptionDetails(null);
        setLoading(false); 
      }
    });

    // Cleanup function
    return () => {
      console.log("AuthContext: Cleaning up auth listener.");
      unsubscribeAuth();
      if (subscriptionListenerUnsubscribe.current) {
        console.log("AuthContext: Detaching Firestore listener on component unmount.");
        subscriptionListenerUnsubscribe.current();
      }
    };
  }, [db]); // Dependency array

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setCurrentUser(userCredential.user);

      setLoading(false); // Loading stops after successful login (listener will handle sub status)
      return userCredential.user;
    } catch (error) {
      console.error("Login process error:", error.code || 'N/A', error.message);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
          setError('Invalid email or password.');
      } else if (error.code === 'auth/too-many-requests') {
          setError('Too many login attempts. Please try again later.');
      } else {
          if (!error) {
            setError(error.message || 'Login failed. Please try again.');
          }
      }
      setLoading(false);
      throw error; 
    } finally {
      setLoading(false);
    }
  };

  const logout = handleLogout; 

  const hasValidSubscription = useCallback(() =>
    subscriptionDetails?.subscription_status === 'active' ||
    subscriptionDetails?.subscription_status === 'trialing',
  [subscriptionDetails?.subscription_status]);

  const RequireAuth = ({ children }) => {
    const { currentUser, subscriptionDetails, isLoading } = useAuth();

    if (isLoading || subscriptionDetails === null) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }
    
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }
    
    const isValidSub = subscriptionDetails?.subscription_status === 'active' || subscriptionDetails?.subscription_status === 'trialing';

    if (!isValidSub) {
      console.log("RequireAuth: User found, but no valid subscription. Showing SubscriptionRequired page.", subscriptionDetails);
      return <SubscriptionRequired status={subscriptionDetails?.subscription_status || 'unknown'} />;
    }

    console.log("RequireAuth: User found with valid subscription. Rendering children.");
    return children;
  };

  const value = {
    currentUser,
    user: currentUser,
    login,
    logout,
    error,
    loading: isLoading,
    subscriptionStatus,
    subscriptionDetails,
    hasValidSubscription,
    RequireAuth,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};